import { ApiCodes, type ApiEnvelope } from './types';

/**
 * Normalized error surfaced to the UI. Because AllExceptionsFilter on the
 * backend maps EVERY error to HTTP 200 + an envelope, we branch on
 * `envelope.code`, never on the HTTP status.
 */
export class AdminApiError extends Error {
  code: number;
  msgCode?: number;
  constructor(message: string, code: number, msgCode?: number) {
    super(message);
    this.name = 'AdminApiError';
    this.code = code;
    this.msgCode = msgCode;
  }
  get isAuth() {
    return this.code === ApiCodes.TOKEN_EXPIRED;
  }
  /** Backend surfaces role denials as code:1 msg:'Insufficient role'. */
  get isForbidden() {
    return this.code === ApiCodes.ERROR && /insufficient role/i.test(this.message);
  }
}

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

// Decoupled hooks so the client does not import the auth store (avoids cycles).
let tokenGetter: () => string | null = () => null;
let unauthorizedHandler: () => void = () => {};

export function configureApiAuth(getToken: () => string | null, onUnauthorized: () => void) {
  tokenGetter = getToken;
  unauthorizedHandler = onUnauthorized;
}

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

function buildUrl(path: string, params?: QueryParams): string {
  const url = `${BASE_URL}${path}`;
  if (!params) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
  }
  const q = qs.toString();
  return q ? `${url}?${q}` : url;
}

interface RequestOptions {
  params?: QueryParams;
  signal?: AbortSignal;
  /** When true, an auth error will NOT trigger the global logout handler (used by the login call). */
  skipAuthRedirect?: boolean;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  const token = tokenGetter();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.params), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new AdminApiError('Network error — could not reach the server.', ApiCodes.NETWORK);
  }

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await res.json()) as ApiEnvelope<T>;
  } catch {
    throw new AdminApiError(`Unexpected response (HTTP ${res.status}).`, ApiCodes.ERROR);
  }

  if (envelope.code === ApiCodes.SUCCESS) {
    return envelope.data;
  }

  if (envelope.code === ApiCodes.TOKEN_EXPIRED && !opts.skipAuthRedirect) {
    unauthorizedHandler();
  }

  throw new AdminApiError(envelope.msg || 'Request failed', envelope.code, envelope.msgCode);
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body, opts),
  del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
};
